import React, { useState } from 'react';
import styled from 'styled-components';
import LinkIcon from '~/assets/styleButton/link.svg';
import EditLinkIcon from '~/assets/styleButton/editLink.svg';
import RemoveLinkIcon from '~/assets/styleButton/trash.svg';
import { normalizeUrl } from '~/lib/normalizeUrl';

interface LinkPopupProps {
  url: string;
  onUrlChange?: (normalizedUrl: string) => void;
  onUrlRemove?: () => void;
  onCancel?: () => void;
  onEditClick?: () => void;
  placeholder?: string;
  forceEditMode?: boolean;
  showIcon?: boolean;
  hideDeleteButton?: boolean;
}

const EditContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DisplayContainer = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const UrlText = styled.span`
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-top: 4px;
  margin-left: 6px;
  font-family: ralewayBold, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 150%;
  color: #2e2e2e;
  text-decoration: underline;
`;

const UrlInputField = styled.input`
  flex: 1 1 0;
  min-width: 0;
  border: none;
  background: #f5f5f5;
  border-radius: 5px;
  padding: 9px;
  font-family: raleway, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  color: #6f6f6f;
  outline: none;

  &::placeholder {
    color: #aaa;
  }
`;

const ActionButton = styled.button`
  flex: 0 0 auto;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  height: 32px;

  &:hover {
    opacity: 0.7;
  }
`;

export const LinkPopup = ({
  url,
  onUrlChange,
  onUrlRemove,
  onCancel,
  onEditClick,
  placeholder = 'Type link',
  forceEditMode,
  showIcon = true,
  hideDeleteButton
}: LinkPopupProps) => {
  const [isEditing, setIsEditing] = useState(!url);
  const [inputValue, setInputValue] = useState(url);

  const showEditMode = forceEditMode || isEditing;

  const handleSubmit = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const normalized = normalizeUrl(trimmed);
    onUrlChange && onUrlChange(normalized);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      if (forceEditMode) {
        onCancel && onCancel();
      } else if (url) {
        setIsEditing(false);
        setInputValue(url);
      } else {
        onCancel && onCancel();
      }
    }
  };

  const handleEditClick = () => {
    if (onEditClick) {
      onEditClick();
      return;
    }
    setInputValue(url);
    setIsEditing(true);
  };

  const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

  if (showEditMode) {
    return (
      <EditContainer
        data-rewrite-portal="true"
        onMouseDown={stopPropagation}
        onMouseUp={stopPropagation}
        onClick={stopPropagation}
      >
        {showIcon && (
          <IconWrapper>
            <LinkIcon />
          </IconWrapper>
        )}
        <UrlInputField
          data-testid="link-input"
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          placeholder={placeholder}
          autoFocus
          onKeyDown={handleKeyDown}
          onFocus={stopPropagation}
          onMouseDown={stopPropagation}
          onClick={stopPropagation}
        />
      </EditContainer>
    );
  }

  return (
    <DisplayContainer data-rewrite-portal="true" data-testid="link-url">
      <UrlText data-testid="link-url-text" title={url}>
        {url}
      </UrlText>
      <ActionButton
        data-testid="url-edit-btn"
        onClick={handleEditClick}
        type="button"
      >
        <EditLinkIcon />
      </ActionButton>
      {!hideDeleteButton && (
        <ActionButton
          data-testid="url-delete-btn"
          onClick={onUrlRemove}
          type="button"
        >
          <RemoveLinkIcon />
        </ActionButton>
      )}
    </DisplayContainer>
  );
};
